import Button, { LoadingButton } from '@atlaskit/button';
import { RequiredAsterisk } from '@atlaskit/form';
import { useAnalytics } from '@databases/analytics';
import DatabasePicker from '@databases/content-picker/DatabasePicker';
import { useDatabaseContent } from '@databases/database-state';
import { defaultStoreUtils } from '@databases/redux';
import {
  BrokenDatabase,
  Database,
  DatabaseField,
  EntryCreateOptions,
  EntryWithContext,
} from '@databases/types/database';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import useView from '@databases/views/useView';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../../i18n';
import DatabaseEntryPicker from '../entryPicker/DatabaseEntryPicker';
import DatabaseValuePicker from '../valuePicker/DatabaseValuePicker';

import { getEmbedUrl } from './embed';
import {
  ReferenceOptionContainer,
  ReferenceOptionHeader,
  ReferencePickerContainer,
  OptionGroup,
  ActionsContainer,
} from './embedReferencePicker.styles';

const OptionHeader: FunctionComponent<{ label: string; isRequired?: boolean }> = ({
  label,
  isRequired,
}) => {
  const { formatMessage } = useIntl();

  return (
    <ReferenceOptionContainer>
      <ReferenceOptionHeader>{label}</ReferenceOptionHeader>
      {isRequired ? (
        <RequiredAsterisk />
      ) : (
        <ReferenceOptionHeader isSubtle={true}>
          {formatMessage(messages['reference-picker.optional'])}
        </ReferenceOptionHeader>
      )}
    </ReferenceOptionContainer>
  );
};

export interface EmbedReferencePickerProps {
  handleCancel: () => void;
  setSelectedDatabase: (value: Database | BrokenDatabase | undefined) => void;
  selectedDatabase: Database | BrokenDatabase | undefined;
  setSelectedEntry: (value: EntryWithContext | EntryCreateOptions | undefined) => void;
  selectedEntry: EntryWithContext | EntryCreateOptions | undefined;
  setSelectedField: (value: DatabaseField | undefined) => void;
  selectedField: DatabaseField | undefined;
  databaseError: boolean;
}

const EmbedReferencePicker: FunctionComponent<EmbedReferencePickerProps> = ({
  setSelectedDatabase,
  selectedDatabase,
  setSelectedEntry,
  selectedEntry,
  setSelectedField,
  selectedField,
  handleCancel,
  databaseError,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();

  const baseUrl = defaultStoreUtils.useEqualSelector((state) => {
    return selectBaseUrl(state);
  });

  const { formatMessage } = useIntl();
  const analyticsClient = useAnalytics();
  const { view } = useView();
  const { spaceKey } = useDatabaseContent();

  const [embedLoading, setEmbedLoading] = useState<boolean>(false);
  const [isEmptySubmit, setIsEmptySubmit] = useState<boolean>(false);

  const onDatabaseChange = useCallback(
    (val: Database | BrokenDatabase | undefined) => {
      if (val) {
        setSelectedDatabase(val);
        setIsEmptySubmit(false);
      } else {
        setSelectedDatabase(undefined);
        setIsEmptySubmit(true);
      }

      setSelectedEntry(undefined);
      setSelectedField(undefined);
    },
    [setSelectedDatabase, setSelectedEntry, setSelectedField]
  );

  const handleSubmit = useCallback(async () => {
    if (selectedDatabase) {
      setEmbedLoading(true);
      const url = await getEmbedUrl(
        selectedDatabase,
        selectedEntry,
        selectedField,
        dispatch,
        store,
        view,
        analyticsClient,
        spaceKey!,
        baseUrl
      );
      window.parent.postMessage(JSON.stringify(url), window.location.origin);
      setEmbedLoading(false);
    } else {
      setIsEmptySubmit(true);
    }
  }, [
    selectedDatabase,
    selectedEntry,
    selectedField,
    dispatch,
    store,
    view,
    analyticsClient,
    spaceKey,
    baseUrl,
  ]);

  return (
    <ReferencePickerContainer>
      <OptionGroup>
        <OptionHeader
          label={formatMessage(messages['reference-database-picker.label'])}
          isRequired
        />
        <DatabasePicker
          onChange={onDatabaseChange}
          selectedDatabase={selectedDatabase}
          isInvalid={databaseError || isEmptySubmit}
        />
      </OptionGroup>

      <OptionGroup>
        <OptionHeader label={formatMessage(messages['reference-entry-picker.label'])} />
        <DatabaseEntryPicker
          selectedDatabase={selectedDatabase}
          setSelectedEntry={setSelectedEntry}
          selectedEntry={selectedEntry}
          setSelectedField={setSelectedField}
          canCreate={selectedDatabase ? selectedDatabase.operations.updateEntries : false}
        />
      </OptionGroup>

      <OptionGroup>
        <OptionHeader label={formatMessage(messages['reference-value-picker.label'])} />
        <DatabaseValuePicker
          selectedDatabase={selectedDatabase}
          selectedEntry={selectedEntry}
          selectedField={selectedField}
          setSelectedField={setSelectedField}
        />
      </OptionGroup>

      <ActionsContainer>
        <Button
          appearance={'subtle'}
          onClick={handleCancel}
          testId={'reference-cancel-button'}
        >
          {formatMessage(messages['reference-picker.cancel'])}
        </Button>
        <LoadingButton
          testId="reference-submit-button"
          type="submit"
          appearance={'primary'}
          isLoading={embedLoading}
          onClick={handleSubmit}
        >
          {formatMessage(messages['reference-picker.embed'])}
        </LoadingButton>
      </ActionsContainer>
    </ReferencePickerContainer>
  );
};
export default EmbedReferencePicker;
